import { Flex, Stack, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { readBanNotice } from '#features/api/utils/banNotice';
import { Link } from '#features/routing';
import { Button, PageHeading, Panel } from '#features/ui';
import { staticPageHead } from '#features/static-pages/utils/head';

const title = 'Account suspended | Teamplay.gg';
const description = 'This account has been suspended for violating the community rules.';

// Intentionally placed outside of `_layout`: the page must work for an anonymous
// visitor (the gateway resets the auth cookie on ban) and must not fire any
// authenticated requests of its own (no MainLayout profile/realtime hooks).
export const Route = createFileRoute('/{-$locale}/banned')({
  component: BannedPage,
  head: () => staticPageHead({ title, description, pathname: '/banned' }),
});

function BannedPage() {
  const { t, i18n } = useLingui();

  /*
    Срок и причину страница не запрашивает, а забирает из записки, оставленной в момент отказа:
    к этому моменту куки уже нет, и спросить сервер ей не у кого. Записки может не быть — если
    человек открыл адрес напрямую или закрыл вкладку, — тогда остаётся общий текст.
  */
  /*
    Читаем ПОСЛЕ монтирования, а не на первом рендере. Записка живёт в хранилище браузера:
    сервер её не видит и рисует общий текст, а клиент на первом же рендере подставлял причину и
    срок — разметка расходилась, и React ронял гидрацию (#418) на странице, куда человек попадает
    и без того в неприятный момент. Одним кадром позже причина появляется без всякого расхождения.
  */
  const [notice, setNotice] = useState<ReturnType<typeof readBanNotice>>(null);

  useEffect(() => {
    setNotice(readBanNotice());
  }, []);

  const reasons: Record<string, string> = {
    ADVERTISING: t`advertising or selling services`,
    SPAM: t`spam or flooding`,
    HARASSMENT: t`harassment or insults`,
    SCAM: t`trying to get money or account access`,
    INAPPROPRIATE: t`inappropriate content`,
    OTHER: t`breaking the community rules`,
  };

  // Причины нет у старых блокировок и при прямом заходе на адрес. Берём тогда общую
  // формулировку из того же списка, а не заводим вторую фразу: две фразы про одно и то же
  // расходятся при первой же правке, и одна из них остаётся без перевода.
  const reason = reasons[notice?.reason ?? 'OTHER'] ?? reasons.OTHER;

  const until = useMemo(() => {
    if (!notice?.bannedUntil) return null;
    const date = new Date(notice.bannedUntil);
    if (Number.isNaN(date.getTime())) return null;
    // Прошедший срок означает, что блокировка уже снялась сама, а записка осталась с прошлого
    // раза — обещать человеку вчерашнюю дату хуже, чем не назвать её вовсе.
    if (date.getTime() <= Date.now()) return null;
    return i18n.date(date, { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
  }, [notice?.bannedUntil, i18n]);

  return (
    <Flex minH="100dvh" alignItems="center" justifyContent="center" px={4} py={8}>
      <Panel
        variant="object"
        display="flex"
        flexDir="column"
        gap={5}
        alignItems="center"
        textAlign="center"
        p={{ base: 6, md: 10 }}
        maxW="480px"
        w="100%"
      >
        <PageHeading>
          {t`Account suspended`}
        </PageHeading>

        <Stack gap={2}>
          <Text color="fg.muted">{t`Your account has been suspended for ${reason}.`}</Text>
          <Text color="fg.muted">
            {until
              ? t`The suspension lifts on ${until}.`
              : t`The suspension has no end date set.`}
          </Text>
          <Text color="fg.muted">
            {t`If you believe this is a mistake, contact us at support@teamplay.gg`}
          </Text>
        </Stack>

        <Button asChild>
          <Link to="/{-$locale}">{t`Go to home`}</Link>
        </Button>
      </Panel>
    </Flex>
  );
}
